export const MODAL_HASH_PARAM = "modal";

export interface HashModalOptions {
  ownerId?: string;
}

export interface ActiveHashModal {
  modal: string;
  ownerId: string | null;
  payload: unknown;
  pushed: boolean;
}

export type HashModalState<TPayload> =
  | { open: false; payload: null }
  | { open: true; payload: TPayload };

export interface HashModalLocationParts {
  pathname: string;
  search: string;
  hash: string;
}

export type HashModalCloseAction = "back" | "replace";

export function getOwnerId(options?: HashModalOptions) {
  return options?.ownerId ?? null;
}

export function getHashParams(hash: string) {
  return new URLSearchParams(hash.replace(/^#/, ""));
}

export function getModalHash(hash: string) {
  return getHashParams(hash).get(MODAL_HASH_PARAM);
}

export function getUrlWithModalHash(location: HashModalLocationParts, modal: string) {
  const params = getHashParams(location.hash);
  params.set(MODAL_HASH_PARAM, modal);
  return `${location.pathname}${location.search}#${params.toString()}`;
}

export function getUrlWithoutModalHash(location: HashModalLocationParts) {
  const params = getHashParams(location.hash);
  params.delete(MODAL_HASH_PARAM);
  const hash = params.toString();
  return `${location.pathname}${location.search}${hash ? `#${hash}` : ""}`;
}

export function isMatchingModal(
  activeModal: ActiveHashModal | null,
  modal: string,
  options?: HashModalOptions,
) {
  return activeModal?.modal === modal && activeModal.ownerId === getOwnerId(options);
}

export function getStateFromActiveModal<TPayload>(
  activeModal: ActiveHashModal | null,
  modal: string,
  options?: HashModalOptions,
): HashModalState<TPayload> {
  if (!activeModal || !isMatchingModal(activeModal, modal, options)) {
    return { open: false, payload: null };
  }
  return { open: true, payload: activeModal.payload as TPayload };
}

export function shouldClearUnsupportedModalHash(
  activeModal: ActiveHashModal | null,
  modalHash: string | null,
) {
  return modalHash !== null && activeModal?.modal !== modalHash;
}

export function getCloseModalHistoryAction({
  pushed,
  historyLength,
}: {
  pushed: boolean;
  historyLength: number;
}): HashModalCloseAction {
  return pushed && historyLength > 1 ? "back" : "replace";
}
